/**
 * Resolves and decodes the baked sound effects into {@link AudioBuffer}s keyed by
 * their content id, the audio analogue of {@link loadEquipFrames}: the wav set is
 * discovered with `import.meta.glob` over `assets/baked/sound/`, which the stub (or
 * the real converter) fills by walking the SAME {@link sounds} registry, so what is
 * importable here can never drift from what is baked. The wavs are gitignored
 * copyrighted UO audio; a clientless host has silent placeholders, so this resolves
 * (and decodes) to silence rather than failing - the pipeline stays wired.
 *
 * Since issue #626 the decode is **per sound, on demand**: the first audio gesture
 * no longer decodes the whole registry - the {@link ./soundResidency.SoundResidency}
 * layer calls {@link fetchSoundBuffer} lazily/for warm sets and bounds what stays
 * decoded. A missing or undecodable wav resolves `undefined` (with a warning), which
 * the residency layer records as terminal absence - degraded once, never retried.
 */
import type { SoundId } from './soundObserver';

// The baked wav urls, discovered the same way the equip atlases are - glob'd
// because the set is registry-driven. Vite resolves these at build/test time
// against `assets/baked/sound/`, keyed by relative path; re-keyed to `<id>` below.
const soundUrls = import.meta.glob('../../assets/baked/sound/*.wav', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

/** Re-key a glob result by its filename without extension (the sound `<id>`). */
function byId<T>(glob: Record<string, T>): Map<string, T> {
  const out = new Map<string, T>();
  for (const [path, value] of Object.entries(glob)) {
    const file = path.slice(path.lastIndexOf('/') + 1).replace(/\.wav$/, '');
    out.set(file, value);
  }
  return out;
}

const urlsBySoundId = byId(soundUrls);

/**
 * Fetch and decode ONE baked sound into an {@link AudioBuffer}, resolving
 * `undefined` (with a warning, never a throw) when the wav is missing or fails to
 * decode - one bad file never kills the rest of the soundscape. The lazy per-id
 * decode seam behind {@link ./soundResidency.SoundResidency}.
 */
export async function fetchSoundBuffer(
  ctx: AudioContext,
  id: SoundId | string,
): Promise<AudioBuffer | undefined> {
  const url = urlsBySoundId.get(id);
  if (!url) {
    console.warn(`no baked wav for sound "${id}" - skipped`);
    return undefined;
  }
  try {
    const data = await fetch(url).then((r) => r.arrayBuffer());
    return await ctx.decodeAudioData(data);
  } catch (err) {
    console.warn(`failed to decode sound "${id}":`, err);
    return undefined;
  }
}
